import React, { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { get } from 'lodash';
import { ChevronDown, ChevronUp, Paperclip, Trash2, X } from 'lucide-react';
import CurrencyInput from '../../../../../components/CurrencyInput';

import { OrderSchemaType } from '../order.schema';
import { DETAILS_FORM_DATA } from '../constants';
import { useAttachmentHandler } from '../Hooks/useAttachmentHandler';
import { useOrderCalculations } from '../Hooks/useOrderCalculations';

import {
  Title,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Select,
  CollapsibleHeader,
  CircularCollapseIcon,
  Separator,
  AttachmentList,
  AttachmentListItem,
  RemoveAttachmentButton,
  AttachmentInputContainer,
  AttachmentTrigger,
  AttachmentPlaceholder,
  TrashIconContainer,
  Summary,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
} from '../style';

const DetailsSection: React.FC = () => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);

  const { control, register, formState: { errors } } = useFormContext<OrderSchemaType>();

  const {
    attachments,
    attachmentError,
    fileInputRef,
    handleFileSelectClick,
    handleFileChange,
    handleRemoveAttachment,
    handleRemoveAllAttachments,
  } = useAttachmentHandler({ fieldPath: 'attachments' });

  const { totalQuantity, taxes, total } = useOrderCalculations();

  return (
    <>
      <CollapsibleHeader onClick={() => setIsDetailsOpen(prev => !prev)}>
        <Title>Detalhes do pedido</Title>
        <CircularCollapseIcon onClick={(e) => { e.stopPropagation(); setIsDetailsOpen(prev => !prev); }}>
          {isDetailsOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </CircularCollapseIcon>
      </CollapsibleHeader>

      {isDetailsOpen && (
        <>
          <FormGrid>
            <FormGroup>
              <Label htmlFor="orderType">Tipo de Pedido</Label>
              <Controller
                name="orderType"
                control={control}
                render={({ field }) => (
                  <Select id="orderType" {...field} hasError={!!get(errors, 'orderType')}>
                    <option value="">Selecione</option>
                    {DETAILS_FORM_DATA.orderTypes.map(type => <option key={type} value={type}>{type}</option>)}
                  </Select>
                )}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="orderDate">Data do Pedido</Label>
              <Input id="orderDate" type="date" {...register('orderDate')} hasError={!!get(errors, 'orderDate')} />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="deliveryDate">Prazo de Entrega</Label>
              <Input id="deliveryDate" type="date" {...register('deliveryDate')} hasError={!!get(errors, 'deliveryDate')} />
            </FormGroup>
          </FormGrid>

          <FormGrid style={{ marginTop: '20px' }}>
            <FormGroup>
              <Label htmlFor="paymentTerm">Condição de Pagamento</Label>
              <Controller
                name="paymentTerm"
                control={control}
                render={({ field }) => (
                  <Select id="paymentTerm" {...field} hasError={!!get(errors, 'paymentTerm')}>
                    <option value="">Selecione</option>
                    {DETAILS_FORM_DATA.paymentTerms.map(term => <option key={term} value={term}>{term}</option>)}
                  </Select>
                )}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="shippingType">Tipo de Frete</Label>
              <Controller
                name="shippingType"
                control={control}
                render={({ field }) => (
                  <Select id="shippingType" {...field} hasError={!!get(errors, 'shippingType')}>
                    <option value="">Selecione</option>
                    {DETAILS_FORM_DATA.shippingTypes.map(type => <option key={type} value={type}>{type}</option>)}
                  </Select>
                )}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="shippingValue">Valor do Frete</Label>
              <Controller
                name="shippingValue"
                control={control}
                render={({ field }) => (
                  <CurrencyInput
                    id="shippingValue"
                    name={field.name}
                    value={field.value || 0}
                    onValueChange={(value, name, values) => {
                      field.onChange(values?.float || 0);
                    }}
                    placeholder="R$ 0,00"
                    hasError={!!get(errors, 'shippingValue')}
                  />
                )}
              />
            </FormGroup>
          </FormGrid>

          <FormGrid>
            <FormGroup>
              <Label>Código de rastreio</Label>
              <Input {...register('trackingCode')} placeholder="Digite" />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="attachments">Anexos</Label>
              <AttachmentInputContainer>
                <AttachmentTrigger onClick={handleFileSelectClick} hasError={!!attachmentError}>
                  <Paperclip size={16} />
                  <AttachmentPlaceholder>Clique para anexar arquivo</AttachmentPlaceholder>
                </AttachmentTrigger>
                {attachments.length > 0 && (
                  <TrashIconContainer
                    as="button"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveAllAttachments();
                    }}
                  >
                    <Trash2 size={16} />
                  </TrashIconContainer>
                )}
              </AttachmentInputContainer>
              {attachments.length > 0 && (
                <AttachmentList>
                  {attachments.map((file, index) => (
                    <AttachmentListItem key={index}>
                      {file.name}
                      <RemoveAttachmentButton
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRemoveAttachment(index);
                        }}
                      >
                        <X size={12} />
                      </RemoveAttachmentButton>
                    </AttachmentListItem>
                  ))}
                </AttachmentList>
              )}
              <input
                type="file"
                multiple
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept=".pdf,.xml,.doc,.docx,.xls,.xlsx,.jpeg,.jpg,.png"
              />
              {attachmentError && <p style={{ color: 'red', fontSize: '12px' }}>{attachmentError}</p>}
            </FormGroup>

            <FormGroup>
              <Label>Informação extra</Label>
              <Input {...register('extraInfo')} placeholder="Digite" />
            </FormGroup>
          </FormGrid>
        </>
      )}
      <Separator />

      <Summary>
        <SummaryItem>
          <SummaryLabel>Quantidade total:</SummaryLabel>
          <SummaryValue>{totalQuantity || 0}</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Tributos:</SummaryLabel>
          <SummaryValue>
            {(taxes || 0).toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })}
          </SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Total:</SummaryLabel>
          <SummaryValue>
            {(total || 0).toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })}
          </SummaryValue>
        </SummaryItem>
      </Summary>
    </>
  );
};

export default DetailsSection;